import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { agentsAPI } from '../services/api';

/**
 * What this application knows about one trust, beside what the DC reports.
 *
 * The directory is authoritative on whether a trust exists and its type and
 * direction. It cannot answer the questions asked months later -- who set this
 * up, on which addresses, who last checked it and what did it say -- so those
 * are recorded here and shown together.
 *
 * Editing is limited to the addresses and the notes ON PURPOSE. Type and
 * direction are properties of the directory that Samba cannot alter in place;
 * a form that appeared to change them would be lying, so the dialog says what
 * changing them actually requires instead of offering a field.
 */

function when(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function Field({ label, value, missing }) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}
                  color={value ? 'text.primary' : 'text.secondary'}>
        {value || missing || '—'}
      </Typography>
    </Grid>
  );
}

function TrustDetails({ agentName, domain, isAdmin, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ local_ip: '', remote_ip: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!agentName || !domain) return;
    try {
      const res = await agentsAPI.trustDetail(agentName, domain);
      setDetail(res.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load the trust record.');
    }
  }, [agentName, domain]);

  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setForm({
      local_ip: detail?.local_ip || '',
      remote_ip: detail?.remote_ip || '',
      notes: detail?.notes || '',
    });
    setEditOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Send blanks as null so clearing a field actually clears it.
      await agentsAPI.updateTrustDetail(agentName, domain, {
        local_ip: form.local_ip.trim() || null,
        remote_ip: form.remote_ip.trim() || null,
        notes: form.notes,
      });
      setEditOpen(false);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  if (error && !detail) {
    return <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>;
  }
  if (!detail) return null;

  const validated = detail.last_validated_at;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Stack direction="row" alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Recorded details
        </Typography>
        {validated && (
          <Tooltip title={detail.last_validation_detail || ''}>
            <Chip
              size="small"
              color={detail.last_validation_ok ? 'success' : 'error'}
              label={detail.last_validation_ok ? 'last check passed' : 'last check failed'}
            />
          </Tooltip>
        )}
        {isAdmin && (
          <Button size="small" startIcon={<EditIcon />} onClick={openEdit}>
            Edit
          </Button>
        )}
      </Stack>

      <Grid container spacing={1.5}>
        <Field label="Type" value={detail.trust_type} />
        <Field
          label="SID filtering"
          value={detail.quarantined === 'yes' ? 'ON (quarantined)'
                 : detail.quarantined === 'no' ? 'OFF' : null}
          missing="unknown"
        />
        <Field label="Direction" value={detail.direction} />
        <Field label="Established" value={when(detail.established_at)} />
        <Field
          label="Established by"
          value={detail.established_by}
          missing={detail.established_by_note || 'not recorded'}
        />
        <Field label="This domain's address" value={detail.local_ip}
               missing="not recorded" />
        <Field label="Remote domain's address" value={detail.remote_ip}
               missing="not recorded" />
        <Field label="Last validated" value={when(detail.last_validated_at)}
               missing="never validated from here" />
        <Field label="Last validated by" value={detail.last_validated_by} />
        {detail.notes && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Notes
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {detail.notes}
            </Typography>
          </Grid>
        )}
      </Grid>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit recorded details for {domain}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            These are this application's notes, not the trust itself. The
            trust's <strong>type</strong> and <strong>direction</strong> live in
            the directory and Samba cannot change them in place — changing
            either means deleting the trust and creating it again.
          </Alert>
          <TextField
            fullWidth margin="dense" label="This domain's address"
            helperText="The address the REMOTE side actually reaches this DC on — often not what either domain's DNS says."
            value={form.local_ip}
            onChange={(e) => setForm({ ...form, local_ip: e.target.value })}
          />
          <TextField
            fullWidth margin="dense" label="Remote domain's address"
            helperText="The address this DC actually reaches the remote one on."
            value={form.remote_ip}
            onChange={(e) => setForm({ ...form, remote_ip: e.target.value })}
          />
          <TextField
            fullWidth margin="dense" label="Notes" multiline minRows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TrustDetails;
